import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import { Badge, Row } from 'reactstrap';

import { resizeImage } from '@techchange/filestack';
import { tcBuildSessionURL } from '@techchange/components';
import { messages } from '../../../i18n';

import styles from './NavSearchResultsOption.module.scss';

const NUM_DEFAULT_BADGE_COLORS = 10;

const propTypes = {

	/**
	 * The enrollment data for the search option.
	 */
	data: PropTypes.shape({

		/**
		 * The enrollment ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The selected course of the enrollment.
		 */
		course: PropTypes.shape({

			/**
			 * The course ID.
			 */
			id: PropTypes.string.isRequired,

			/**
			 * The course title.
			 */
			title: PropTypes.string.isRequired,

			/**
			 * The course badge (if any).
			 */
			badge: PropTypes.string,

		}).isRequired,

		/**
		 * The selected session of the enrollment.
		 */
		session: PropTypes.shape({

			/**
			 * The session ID.
			 */
			id: PropTypes.string.isRequired,

			/**
			 * The session title.
			 */
			title: PropTypes.string.isRequired,

			/**
			 * The session slug.
			 */
			slug: PropTypes.string.isRequired,

			/**
			 * The session start date (if any).
			 */
			start_date: PropTypes.string,

			/**
			 * The session end date (if any).
			 */
			end_date: PropTypes.string,

			/**
			 * True if the session is open.
			 */
			is_open: PropTypes.bool,

		}).isRequired,

	}).isRequired,

	/**
	 * The list of all the search options.
	 */
	options: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The enrollment ID of the search option.
		 */
		id: PropTypes.string.isRequired,

	})).isRequired,

	/**
	 * True, if the current user is an administrator.
	 */
	isAdmin: PropTypes.bool,

};

const NavSearchResultsOption = ({ data, options = [], isAdmin }) => {

	const intl = useIntl();

	if (!data.course || !data.session) {
		return null;
	}

	const badge = data.course.badge;
	const title = data.session.title;


	let backupBadge;

	let backupBadgeColor = 'default-color-0';

	// Check if this search option is missing a course badge
	if (!badge) {

		backupBadge = title && title.charAt(0) && title.charAt(0).toUpperCase();

		// Calculate the index of the option relative to all the search options
		const index = options.findIndex((option) => option.id === data.id);

		// Choose from one of the default colors for the badge of this search option
		if (index >= 0) {
			backupBadgeColor = `default-color-${index % NUM_DEFAULT_BADGE_COLORS}`;
		}

	}

	return (
		<Row
			className={`align-items-center g-0 p-3 ${styles['nav-searchbar-results-option']}`}>
			<a
				tabIndex="-1"
				href={tcBuildSessionURL(data.session)}
				className={`col d-flex ${styles['nav-searchbar-results-option-link']}`}
				title={intl.formatMessage(messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_GENERAL_TITLE_TEXT_LINK_TO_COURSE, { title })}>
				<div className={styles['search-results-img']}>
					{badge &&
						<img
							src={resizeImage(badge, 'width', 50)}
							srcSet={`
							${resizeImage(badge, 'width', 50)} 1x,
							${resizeImage(badge, 'width', 100)} 2x
						`}
							alt={title} />
					}
					{!badge &&
						<div className={`${styles['is-missing-img']} ${styles[backupBadgeColor]}`}>
							{backupBadge}
						</div>
					}
				</div>
				<div className={styles['search-results-content']}>
					<span>{title}
						{!data.session.is_open &&
							<Badge
								color="secondary"
								className="ms-2">
								<FormattedMessage {...messages.TC_COMPONENTS_GENERAL_TEXT_CLOSED} />
							</Badge>
						}
					</span>
					<span
						className={`text-muted ${styles['search-results-info']}`}>
						{getEnrollmentInfo(data, intl)}
					</span>
				</div>
			</a>
			{isAdmin &&
				<a
					tabIndex="-1"
					href={`/admin/courses/${data.course.id}/sessions/${data.session.id}`}
					title={intl.formatMessage(messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_GENERAL_TITLE_TEXT_LINK_TO_ADMIN)}
					className={`col-auto align-items-center ${styles['search-results-link-to-admin']}`}>
					<FormattedMessage {...messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_SEARCH_DROPDOWN_MENU_RESULTS_OPTION_TEXT_LINK_TO_ADMIN} />
					{' '}
					<i
						aria-hidden="true"
						className="far fa-external-link" />
				</a>
			}
		</Row>
	);
};

NavSearchResultsOption.propTypes = propTypes;

export default NavSearchResultsOption;

/**
 * Provides extra information for context related to the enrollment, i.e. the type.
 *
 * @param   {object} enrollment  The enrollment.
 * @param   {object} intl        The provider for internationalization.
 * @returns {string} result      Text, which provides context for the enrollment.
 */
function getEnrollmentInfo(enrollment, intl) {

	const session = enrollment.session;

	if (!session) {
		return null;
	}

	const startDate = session.start_date ? moment(session.start_date) : null;
	const endDate = session.end_date ? moment(session.end_date) : null;

	const format = (date) => date.format('MMM D, YYYY');

	const now = moment();

	let result = '';

	// Check if the session has both a "start" and "end" date.
	if (startDate && endDate) {

		// Check if the "start" and "end" date are the same
		if (startDate.isSame(endDate, 'day')) {

			result += intl.formatMessage(messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_GENERAL_TEXT_SESSION_SAME_DAY, {
				date: format(startDate),
			});

		} else {

			result += intl.formatMessage(messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_GENERAL_TEXT_SESSION_START_TO_END, {
				start: format(startDate),
				end: format(endDate),
			});

		}

	// Check if the session has a "start" date, but no "end" date.
	} else if (startDate && !endDate) {

		// Check if the session hasn't started yet
		if (now.isBefore(startDate)) {

			result += intl.formatMessage(messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_GENERAL_TEXT_SESSION_STARTS, {
				date: format(startDate),
			});

		} else {

			result += intl.formatMessage(messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_GENERAL_TEXT_SESSION_STARTED, {
				date: format(startDate),
			});

		}

	// Check if the session has an "end" date, but not "start" date.
	} else if (!startDate && endDate) {

		// Check if the session has already ended
		if (now.isAfter(endDate)) {

			result += intl.formatMessage(messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_GENERAL_TEXT_SESSION_ENDED, {
				date: format(endDate),
			});

		} else {

			result += intl.formatMessage(messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_GENERAL_TEXT_SESSION_ENDS, {
				date: format(endDate),
			});

		}

	}

	// Otherwise, the session is ongoing, i.e. no "start" date and no "end" date
	return result;

}
